import { useState, useEffect } from "react";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Trash2, Globe, Sparkles, X, Loader2, Pencil } from "lucide-react";
import ListToolbar from "@/components/ListToolbar";

const EMPTY_FORM = { name: "", rut: "", contact_name: "", contact_email: "", website: "" };

function ClientCard({ c, analyzing, onAnalyze, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all" data-testid={`client-${c.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-slate-900 truncate">{c.name}</h3>
            {c.rut && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono flex-shrink-0">{c.rut}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
            {c.contact_name && <span>{c.contact_name}</span>}
            {c.contact_email && <span>{c.contact_email}</span>}
            {c.website && <span className="flex items-center gap-1"><Globe size={14} />{c.website}</span>}
          </div>
          {c.ai_context && (
            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid={`ai-context-${c.id}`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 mb-1"><Sparkles size={12} /> Análisis IA</div>
              <p className="text-sm text-slate-700 leading-relaxed">{c.ai_context}</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          {c.website && (
            <button onClick={() => onAnalyze(c.id)} disabled={analyzing === c.id}
              className="rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-2 text-xs font-medium transition-all flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`analyze-${c.id}`}>
              {analyzing === c.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {analyzing === c.id ? "..." : "Analizar"}
            </button>
          )}
          <button onClick={() => onEdit(c)} className="rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-2 transition-all" data-testid={`edit-${c.id}`} title="Editar cliente">
            <Pencil size={16} />
          </button>
          <button onClick={() => onDelete(c.id)} className="rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 p-2 transition-all" data-testid={`delete-${c.id}`}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("cotflow_view_clients") || "card");
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => api.get("/clients").then(r => setClients(r.data)).catch(() => toast.error("Error cargando clientes")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      rut: c.rut || "",
      contact_name: c.contact_name || "",
      contact_email: c.contact_email || "",
      website: c.website || "",
    });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditingId(null); setForm(EMPTY_FORM); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, form);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente creado");
      }
      closeModal();
      load();
    } catch { toast.error(editingId ? "Error al actualizar cliente" : "Error al crear cliente"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente eliminado");
    load();
  };

  const handleAnalyze = async (id) => {
    setAnalyzing(id);
    try {
      const res = await api.post(`/clients/${id}/analyze`);
      toast.success("Análisis completado");
      setClients(prev => prev.map(c => c.id === id ? { ...c, ai_context: res.data.ai_context } : c));
    } catch (err) { toast.error(err.response?.data?.detail || "Error en análisis"); }
    finally { setAnalyzing(null); }
  };

  const filtered = clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || (c.contact_name || "").toLowerCase().includes(search.toLowerCase()) || (c.contact_email || "").toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black font-['Satoshi'] tracking-tight text-slate-900">Clientes</h1>
          <p className="text-slate-500 mt-1">{clients.length} clientes registrados</p>
        </div>
        <button onClick={openCreate} className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 shadow-sm font-medium px-5 py-2.5 transition-all active:scale-95 flex items-center gap-2 text-sm" data-testid="add-client-btn">
          <Plus size={18} /> Nuevo Cliente
        </button>
      </div>

      <ListToolbar search={search} onSearch={setSearch} viewMode={viewMode} onViewMode={m => { setViewMode(m); localStorage.setItem("cotflow_view_clients", m); }} />

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Users size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">{search ? "No se encontraron clientes" : "Aún no tienes clientes"}</p>
        </div>
      ) : viewMode === "card" ? (
        <div className="space-y-3">
          {filtered.map(c => <ClientCard key={c.id} c={c} analyzing={analyzing} onAnalyze={handleAnalyze} onEdit={openEdit} onDelete={handleDelete} />)}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Empresa</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">RUT</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Contacto</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Email</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Web</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-slate-50/50" data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-5 py-3 text-slate-500 font-mono text-xs">{c.rut || "-"}</td>
                  <td className="px-5 py-3 text-slate-500">{c.contact_name || "-"}</td>
                  <td className="px-5 py-3 text-slate-500">{c.contact_email || "-"}</td>
                  <td className="px-5 py-3 text-slate-500 max-w-[150px] truncate">{c.website || "-"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {c.website && (
                        <button onClick={() => handleAnalyze(c.id)} disabled={analyzing === c.id} className="text-blue-500 hover:text-blue-700 p-1" data-testid={`analyze-row-${c.id}`}>
                          {analyzing === c.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        </button>
                      )}
                      <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`edit-row-${c.id}`} title="Editar cliente"><Pencil size={16} /></button>
                      <button onClick={() => handleDelete(c.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="client-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold font-['Satoshi']">{editingId ? "Editar Cliente" : "Nuevo Cliente"}</h2>
              <button onClick={closeModal} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Nombre empresa *</label>
                <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="client-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">RUT</label>
                  <input type="text" value={form.rut} onChange={e => setForm({ ...form, rut: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="client-form-rut" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Contacto</label>
                  <input type="text" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="client-form-contact" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Email contacto</label>
                <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="client-form-email" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Sitio web</label>
                <input type="text" placeholder="https://..." value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="client-form-website" />
              </div>
              <button type="submit" className="w-full bg-slate-900 text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-all active:scale-[0.98]" data-testid="client-form-submit">
                {editingId ? "Guardar cambios" : "Crear Cliente"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
